import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import { cloneDeep } from 'lodash';
import {
  SpecialFees,
  FeeCategory,
  FixedValueFee,
  FinanceType,
} from '@upstart/harmony/lib/constants';
import { isEmbeddedInCommandCenter } from 'util/commandCenter';
import { Action, ModalType } from 'util/amplitudeEvents';
import DataTable from '../../DataTable';
import Input, { PriceInput, PercentageInput, parseFloatValue } from '../Input';
import {
  scrollableModalStyles,
  SPECIAL_FEES_NAMES,
} from '../../../util/constants';
import { isAprFee } from '../../../util/pencilUtils';
import Select from '../Select/Select';
import './DealerFeesModal.scss';

function DealerFeesModal(props) {
  const {
    disabled,
    onChange,
    translations,
    sendAmplitudeEvent,
    isMarketscanEnabled,
  } = props;

  const [modalIsOpen, setModalIsOpen] = useState(true);
  const closeModal = React.useCallback(() => {
    setModalIsOpen(false);
    props.closeModal();
  }, [setModalIsOpen, props.closeModal]);

  // add a counter to track visual order of fee items
  // add an index to each item which is their position in the visual sequence
  function addVisualIndexToFees(fees) {
    const feesClone = cloneDeep(fees);
    feesClone.visualCounter = feesClone.visualCounter ?? 0;
    Object.values(FeeCategory).forEach(feeCategory => {
      if (feesClone[feeCategory]) {
        feesClone[feeCategory].forEach(fee => {
          if (typeof fee.visualIndex !== 'number') {
            fee.visualIndex = feesClone.visualCounter;
            feesClone.visualCounter += 1;
          }
        });
      }
    });
    return feesClone;
  }

  const [fees, setFees] = useState(addVisualIndexToFees(props.fees));
  // update fees if new ones are given
  useEffect(() => {
    setFees(addVisualIndexToFees(props.fees));
  }, [props.fees]);

  /**
   * Adds a new taxable fee
   */
  function addFee() {
    const updatedFees = cloneDeep(fees);
    // add fees as taxable by default
    updatedFees[FeeCategory.TAXABLE] = updatedFees[FeeCategory.TAXABLE] || [];
    updatedFees.visualCounter += 1;
    updatedFees[FeeCategory.TAXABLE].push({
      type: FixedValueFee,
      name: '',
      value: undefined,
      apr: undefined,
      visualIndex: updatedFees.visualCounter,
    });
    setFees(updatedFees);
  }

  /**
   * Changes a fee property
   *
   * @param feeCategory the category of fee (ex. taxable, nontaxable, etc...)
   * @param feeIndex the index of the fee within `fees[feeCategory]`
   * @param property the fee property to update
   * @param value the new value to set for the fee
   */
  function changeFeeProperty(feeCategory, feeIndex, property, value) {
    const updatedFees = cloneDeep(fees);
    updatedFees[feeCategory][feeIndex][property] = value;
    setFees(updatedFees);
  }

  /**
   * Changes a fee's type
   *
   * @param feeCategory the fee's current category
   * @param feeIndex the index of the fee within `fees[feeCategory]`
   * @param newFeeTypeOrCategory the fee's new type or category
   */
  function changeFeeType(feeCategory, feeIndex, newFeeTypeOrCategory) {
    const fee = cloneDeep(fees[feeCategory][feeIndex]);
    const updatedFees = cloneDeep(fees);
    updatedFees[feeCategory].splice(feeIndex, 1);

    let newFeeCategory = newFeeTypeOrCategory;
    // handle if the new fee type isn't a category
    if (!Object.values(FeeCategory).includes(newFeeTypeOrCategory)) {
      // currently all specific fee types are nontaxable incremental fees
      newFeeCategory = FeeCategory.NON_TAXABLE;

      fee.name = SPECIAL_FEES_NAMES[newFeeTypeOrCategory];
      fee.type = newFeeTypeOrCategory;
      delete fee.value;
      fee.apr = 0;
    } else if (fee.type !== FixedValueFee) {
      fee.name = '';
      fee.type = FixedValueFee;
      fee.value = 0;
      delete fee.apr;
    }
    // Reset total at start flag
    delete fee.addToTotalAtStart;

    updatedFees[newFeeCategory] = updatedFees[newFeeCategory] || [];
    updatedFees[newFeeCategory].push(fee);
    setFees(updatedFees);
  }

  /**
   * Delete a fee
   *
   * We dont need to decrement visualCounter and reassign visualIndex to remaining items.
   * Since it still maintains the sequence, nothing needs to be done wrt to that data in fees.
   *
   * @param feeCategory the category of fee
   * @param feeIndex  the index of the fee within `fees[feeCategory]`
   */
  function deleteFee(feeCategory, feeIndex) {
    const updatedFees = cloneDeep(fees);
    updatedFees[feeCategory].splice(feeIndex, 1);
    setFees(updatedFees);
  }

  /**
   * Save the fees being edited and close the modal
   */
  function saveFees() {
    onChange(fees);
    if (sendAmplitudeEvent) {
      sendAmplitudeEvent(ModalType.DEALER_FEES, Action.FINISH);
    }
    closeModal();
  }

  function renderFeeRow(feeCategory, feeIndex, fee) {
    const typeOrCategory = fee.type === FixedValueFee ? feeCategory : fee.type;
    const disabledForMarketscan =
      isMarketscanEnabled && fee.type !== FixedValueFee;
    const showAddToTotalAtStart =
      props.financeType === FinanceType.LEASE &&
      props.addInceptionFeesToTotalAtStart &&
      props.pencil?.data?.inceptionFeesTotalAtStart?.fees?.enabled;
    return [
      <Select
        value={typeOrCategory}
        onChange={value => changeFeeType(feeCategory, feeIndex, value)}
        disabled={disabled}
      >
        <Select.Option name="Taxable" value={FeeCategory.TAXABLE} />
        <Select.Option name="Non Taxable" value={FeeCategory.NON_TAXABLE} />
        <Select.Option
          name="Capitalized Cost"
          value={FeeCategory.CAPITALIZED_COST}
        />
        <Select.Option
          name="Capitalized Cost Taxable"
          value={FeeCategory.CAPITALIZED_COST_TAXABLE}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.FLORIDA_DOC_STAMP]}
          value={SpecialFees.FLORIDA_DOC_STAMP}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.CALIFORNIA_LICENSE]}
          value={SpecialFees.CALIFORNIA_LICENSE}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.OREGON_CORPORATE_ACTIVITY_TAX]}
          value={SpecialFees.OREGON_CORPORATE_ACTIVITY_TAX}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.OREGON_PRIVILEGE_TAX]}
          value={SpecialFees.OREGON_PRIVILEGE_TAX}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.TEXAS_VEHICLE_INVENTORY_TAX]}
          value={SpecialFees.TEXAS_VEHICLE_INVENTORY_TAX}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.TENNESSEE_BUSINESS_TAX]}
          value={SpecialFees.TENNESSEE_BUSINESS_TAX}
        />
        <Select.Option
          name={SPECIAL_FEES_NAMES[SpecialFees.TEXAS_LENDERS_AND_MEMBERS_FEE]}
          value={SpecialFees.TEXAS_LENDERS_AND_MEMBERS_FEE}
        />
      </Select>,
      <Input
        key={feeIndex}
        onChange={value =>
          changeFeeProperty(feeCategory, feeIndex, 'name', value)
        }
        value={fee.name}
        disabled={disabled || fee.type !== FixedValueFee}
        className="fee-name"
      />,
      // We base special fees directly from Marketscan on the value Marketscan provides hence we don't need to show APR percentage
      isAprFee(fee) && !disabledForMarketscan ? (
        <PercentageInput
          key={feeIndex}
          value={fee.apr}
          onChange={value =>
            changeFeeProperty(feeCategory, feeIndex, 'apr', value)
          }
          disabled={disabled || disabledForMarketscan}
        />
      ) : (
        <PriceInput
          key={feeIndex}
          value={fee.value}
          onChange={value =>
            changeFeeProperty(
              feeCategory,
              feeIndex,
              'value',
              parseFloatValue(value),
            )
          }
          disabled={disabled || disabledForMarketscan}
          className="fee-value"
        />
      ),
      ...(showAddToTotalAtStart &&
      [FeeCategory.NON_TAXABLE, FeeCategory.TAXABLE].includes(feeCategory)
        ? [
            <label>
              <input
                type="checkbox"
                checked={fee.addToTotalAtStart}
                onChange={() => {
                  changeFeeProperty(
                    feeCategory,
                    feeIndex,
                    'addToTotalAtStart',
                    !fee.addToTotalAtStart,
                  );
                }}
                disabled={disabled}
              />{' '}
              Add to Total At Start
            </label>,
          ]
        : []),
      <button
        className="modal-button delete"
        onClick={() => deleteFee(feeCategory, feeIndex)}
        disabled={disabled}
      >
        Delete
      </button>,
    ];
  }

  // consolidate all fee types into one array
  let feeRows = [];
  // The feeRows array contains data to be rendered, whereas
  // feeRowMeta contains corresponding categories and indices.
  // Both arrays are always in sync, while pushing and sorting.
  const feeRowMeta = [];

  Object.values(FeeCategory).forEach(feeCategory => {
    feeRows.push(...(fees[feeCategory] || []));
    // Store feeCategory, visualIndex, feeIndex (index in the fees state object)
    feeRowMeta.push(
      ...(fees[feeCategory] || []).map(({ visualIndex }, index) => ({
        feeCategory,
        visualIndex,
        feeIndex: index,
      })),
    );
  });
  // sort both using same logic
  feeRows.sort((a, b) => a.visualIndex - b.visualIndex);
  feeRowMeta.sort((a, b) => a.visualIndex - b.visualIndex);
  // render feeRows using feeIndex as index
  feeRows = feeRows.map((fee, index) =>
    renderFeeRow(
      feeRowMeta[index].feeCategory,
      feeRowMeta[index].feeIndex,
      fee,
    ),
  );

  return (
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={() => {
        if (sendAmplitudeEvent) {
          sendAmplitudeEvent(ModalType.DEALER_FEES, Action.CANCEL);
        }
        closeModal();
      }}
      contentLabel="Dealer Fees Modal"
      style={scrollableModalStyles}
    >
      <span
        className="close"
        onClick={() => {
          if (sendAmplitudeEvent) {
            sendAmplitudeEvent(ModalType.DEALER_FEES, Action.CANCEL);
          }
          closeModal();
        }}
      >
        {' '}
        X{' '}
      </span>
      <h2>
        {' '}
        {translations?.instore?.['en-US']?.global?.dealerFees || 'Fees'}{' '}
      </h2>
      <DataTable
        className="dealer-fees-table"
        emptyMessage="No fees set"
        rows={feeRows}
        headers={['Type', 'Name', 'Value']}
      />
      <div className="modal-footer">
        {!props.disabled && [
          <button
            key="add"
            className="modal-button add cc-button-outlined"
            onClick={addFee}
            disabled={disabled}
          >
            {isEmbeddedInCommandCenter() ? 'Add more' : 'Add Fee'}
          </button>,
          <button
            key="save"
            className="modal-button save cc-button-primary"
            onClick={saveFees}
            disabled={disabled}
          >
            Save
          </button>,
        ]}
      </div>
    </Modal>
  );
}

DealerFeesModal.propTypes = {
  fees: PropTypes.object,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  closeModal: PropTypes.func,
  financeType: PropTypes.string,
  addInceptionFeesToTotalAtStart: PropTypes.bool,
  pencil: PropTypes.object,
  translations: PropTypes.object,
  sendAmplitudeEvent: PropTypes.func,
  isMarketscanEnabled: PropTypes.bool,
};

export default DealerFeesModal;
